import { computed, ref, type Ref } from 'vue'
import { useVisitorsStore } from '@/shared/store'
import { useIsHolder } from '@/shared/composables/useIsHolder'
import { defaultVisitor, STATE_LIST_INS_KEY, viewsMap } from '@/shared/constants/other.constants'
import { emailResponse } from '@/shared/constants/validationMessages.constants'
import { PurchaseFlowRoutes } from '@/shared/types/router.types'
import type { VisitorType } from '@/shared/types/store.types'
import { newVisitor } from '@/shared/utils'

export type ViewMode = (typeof viewsMap)[keyof typeof viewsMap]

export interface UseVisitorEditFlowDeps {
  visitors: Ref<VisitorType[]>
  isLoading: Ref<boolean>
  isSetHolderNationality: boolean
  redirectToMainPage: () => void
  resolvePreapproval: (visitor: VisitorType) => Promise<VisitorType>
  hasPreapprovalVisa: (visitor: VisitorType) => boolean
  onAddPerson: () => void
  onEditPerson: (visitor: VisitorType) => void
  onDeletePerson: (visitor: VisitorType, view: string) => void
  onSavePerson: (visitor: VisitorType, isAdd: boolean) => void
  onViewQuote: () => void
  onBeforeDelete: () => void
  onPostSaveInbound: (visitor: VisitorType) => Promise<void>
  onPostDeleteInbound: () => Promise<void>
  onLoad: (cb: () => Promise<void>, requestId?: string, withLoader?: boolean) => void
  onProceed: (route?: string) => void
}

export const useVisitorEditFlow = (deps: UseVisitorEditFlowDeps) => {
  const visitorsStore = useVisitorsStore()

  const view = ref<ViewMode>(viewsMap.QUOTE)
  const currentVisitor = ref<VisitorType>(defaultVisitor)
  const emailIsValid = ref(true)

  const editVisitorId = computed(() => visitorsStore.editVisitorId ?? currentVisitor.value.srNo)
  const isHolder = computed(
    () => currentVisitor.value && useIsHolder(currentVisitor.value.relationship)
  )
  const isEdit = computed(() => view.value === viewsMap.EDIT_VISITOR)
  const visitorsMap = computed(() => visitorsStore.visitorsMap)

  const matchingCount = () => visitorsStore.visitorsList.filter((v) => v.durationMatch !== 0).length

  const refreshQuoteAndDetails = async () => {
    await visitorsStore.getQuote(matchingCount())
    await visitorsStore.updateQuote()
    const data = await visitorsStore.getQuoteDetails()
    deps.visitors.value = data
    if (
      !Object.prototype.hasOwnProperty.call(visitorsStore.getQuoteData, 'schemes') ||
      !deps.visitors.value[0]?.firstName
    ) {
      deps.redirectToMainPage()
      return
    }
    deps.onViewQuote()
  }

  const addVisitor = () => {
    currentVisitor.value = newVisitor(defaultVisitor)
    if (deps.isSetHolderNationality) {
      currentVisitor.value.nationality = visitorsStore.holder.nationality
    }

    view.value = viewsMap.ADD_VISITOR
    localStorage.setItem(STATE_LIST_INS_KEY, view.value)
    deps.onAddPerson()
  }

  const editVisitor = ({ srNo }: { srNo: VisitorType['srNo'] }) => {
    visitorsStore.setEditVisitorId(srNo)
    if (editVisitorId.value) {
      const found = visitorsMap.value.get(editVisitorId.value)
      currentVisitor.value = found || defaultVisitor
      view.value = viewsMap.EDIT_VISITOR
      localStorage.setItem(STATE_LIST_INS_KEY, view.value)
      deps.onEditPerson(currentVisitor.value)
    }
  }

  const deleteVisitor = (visitor: VisitorType) => {
    deps.onLoad(async () => {
      deps.onBeforeDelete()
      await visitorsStore.removeTraveller(visitor)
      deps.onDeletePerson(visitor, view.value)
      view.value = viewsMap.QUOTE
      await visitorsStore.getTravellers()
      await refreshQuoteAndDetails()
      await deps.onPostDeleteInbound()
    }, 'quote-delete-visitor')
  }

  const saveVisitor = (visitorInfo: VisitorType) => {
    deps.onLoad(
      async () => {
        deps.isLoading.value = true
        let visitor = visitorInfo
        const isAdd = view.value === viewsMap.ADD_VISITOR
        localStorage.setItem(STATE_LIST_INS_KEY, view.value)

        visitor = await deps.resolvePreapproval(visitor)

        let shouldRefresh = true
        try {
          await visitorsStore.updateVisitor(newVisitor(visitor))
          deps.onSavePerson(visitor, isAdd)
          await visitorsStore.getTravellers()

          if (useIsHolder(visitor.relationship) && deps.hasPreapprovalVisa(visitor)) {
            deps.onProceed(PurchaseFlowRoutes.TRAVEL_DURATION)
            shouldRefresh = false
          }

          currentVisitor.value = defaultVisitor
          view.value = viewsMap.QUOTE
          localStorage.removeItem(STATE_LIST_INS_KEY)
        } catch (error: unknown) {
          shouldRefresh = false
          const message = (error as { response?: { data?: { errorMessage?: string } } })?.response
            ?.data?.errorMessage
          emailIsValid.value = message !== emailResponse
          await visitorsStore.getTravellers()
        } finally {
          deps.isLoading.value = false
        }

        if (!shouldRefresh) return

        await refreshQuoteAndDetails()
        await deps.onPostSaveInbound(visitor)
      },
      'quote-save-visitor',
      !deps.isLoading.value
    )
  }

  const restoreFromLocalStorage = () => {
    const statePage = localStorage.getItem(STATE_LIST_INS_KEY)
    if (!statePage) return
    if (statePage === viewsMap.EDIT_VISITOR || statePage === viewsMap.ADD_VISITOR) {
      const found = visitorsMap.value.get(editVisitorId.value)
      currentVisitor.value =
        statePage === viewsMap.ADD_VISITOR ? defaultVisitor : found || defaultVisitor
      view.value = statePage as ViewMode
    }
  }

  const resetEmailValidity = () => {
    emailIsValid.value = true
  }

  return {
    view,
    currentVisitor,
    editVisitorId,
    isHolder,
    isEdit,
    emailIsValid,
    addVisitor,
    editVisitor,
    deleteVisitor,
    saveVisitor,
    restoreFromLocalStorage,
    resetEmailValidity,
  }
}
